import React, { useState, useEffect } from "react";

import { Grid, MenuItem, IconButton, OutlinedInput, SelectChangeEvent } from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FiTrash2, FiPlusCircle } from "react-icons/fi";

import Button, { Color } from "../../../../../../../../../components/Form/Button";
import Select from "../../../../../../../../../components/Form/Select";
import SkeletonComponent from "../../../../../../../../../components/Generic/Skeleton";
import { NotificationToast } from "../../../../../../../../../components/Generic/Toast/NotificationToast";
import UITable from "../../../../../../../../../components/Generic/UITable";
import { useAppState } from "../../../../../../../../../context/StateContext";
import { Branch } from "../../../../../../../../../models/branch";
import BranchService from "../../../../../../../../../services/branch.service";
import { Status } from "../../../../../../../../../util/enums";
import useStyles, { BranchBox, BranchChip, BranchCode } from "./styles";

interface Props {
  isOnViewMode: boolean;
  isLoading?: boolean;
  branchesList?: Branch[];
  error?: boolean;
  errorMessage?: string;
  updateBranchesOnWarehouse?: (branches: Branch[]) => void;
}

const Branches = ({
  isOnViewMode = false,
  isLoading = false,
  branchesList,
  updateBranchesOnWarehouse,
  error,
  errorMessage,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<Branch[]>([]);
  const [selectedBranchesToAdd, setSelectedBranchesToAdd] = useState<Branch[]>([]);
  const [rows, setRows] = useState<number[]>([]);

  const disabledFields = isOnViewMode || isLoading;

  const { isFetching: fetchingBranches } = useQuery(
    ["branches.list"],
    () =>
      BranchService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setBranches(response.data);
      },
    }
  );

  const handleAddAll = () => {
    setSelectedBranchesToAdd([...branches]);
  };

  const handleRemoveAll = () => {
    setSelectedBranchesToAdd([]);
  };

  const handleSelectedBranch = (branchesId: number[]) => {
    if (branchesId.includes(0)) handleAddAll();
    else if (branchesId.includes(-1)) handleRemoveAll();
    else {
      const branchArr: Branch[] = [];

      for (const branchId of branchesId) {
        const findBranch = branches.find((branch) => branch.id === Number(branchId));

        if (findBranch) branchArr.push(findBranch);
      }
      setSelectedBranchesToAdd([...branchArr]);
    }
  };

  const handleAddBranchToList = () => {
    if (selectedBranchesToAdd && selectedBranchesToAdd.length) {
      const branchesAlreadyAdded: Branch[] = [];
      const branchesId = selectedBranches.map((branch) => branch.id);

      selectedBranchesToAdd.forEach((branch) => {
        if (branchesId.includes(branch.id)) branchesAlreadyAdded.push(branch);
      });

      if (branchesAlreadyAdded.length) {
        NotificationToast.warning(
          `${translate("branchAlreadyExistInTheList")}: ${branchesAlreadyAdded
            .map((branch) => branch.name)
            .join(", ")}`
        );
      } else {
        setSelectedBranches((state) => [...(state || []), ...selectedBranchesToAdd]);
        updateBranchesOnWarehouse?.(
          selectedBranches ? [...selectedBranches, ...selectedBranchesToAdd] : selectedBranchesToAdd
        );
        setSelectedBranchesToAdd([]);
      }
    }
  };

  const handleDeleteListOfBranches = () => {
    const branchesToDelete = selectedBranches;

    rows.forEach((value) => {
      branchesToDelete[value] = {} as Branch;
    });
    const filteredBranch = branchesToDelete.filter(
      (branchItem) => Object.keys(branchItem).length > 0
    );

    setSelectedBranches([...filteredBranch]);
    setRows([]);
    if (updateBranchesOnWarehouse) updateBranchesOnWarehouse([...filteredBranch]);
  };

  const renderChipValue = (selected: number[]) => {
    const selectedBranches: Branch[] = [];

    for (const branchId of selected) {
      const findBranch = branches.find((branch) => branch.id === branchId);

      if (findBranch) selectedBranches.push(findBranch);
    }

    return (
      <BranchBox>
        {selectedBranches?.map((value: Branch, index: number) => {
          if (index <= 1) return <BranchChip key={value.id} label={value.name} />;

          return null;
        })}
      </BranchBox>
    );
  };

  const renderHeaderActions = () => (
    <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
      {rows.length > 0 && (
        <Grid item container md={3} lg={3} justifyContent="flex-end">
          <Button
            className={classes.button}
            customColor={Color.ERROR}
            onClick={() => handleDeleteListOfBranches()}
            disabled={disabledFields}
          >
            <FiTrash2 size={18} className="mr-2" />
            {translate("deleteSelected")}
          </Button>
        </Grid>
      )}
      <Grid item container md={5} lg={5} justifyContent="flex-end">
        <SkeletonComponent classesName={classes.loading} isLoading={fetchingBranches}>
          <Select
            dataCy="selectBranch"
            name="branchList"
            className={classes.select}
            value={
              selectedBranchesToAdd && selectedBranchesToAdd?.length
                ? selectedBranchesToAdd?.map((branch) => branch?.id || -2)
                : []
            }
            onChange={(event: SelectChangeEvent<any>) => handleSelectedBranch(event.target.value)}
            input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
            multiple
            renderValue={(selected) => renderChipValue(selected as number[])}
          >
            {selectedBranchesToAdd.length < branches?.length && (
              <MenuItem value={0}>
                <span className={classes.listSpan}>
                  <AddCircleOutline className="mr-3" />
                  {translate("addAll")}
                </span>
              </MenuItem>
            )}
            {selectedBranchesToAdd.length === branches?.length && (
              <MenuItem value={-1}>
                <span className={classes.listSpan}>
                  <RemoveCircleOutline className="mr-3" />
                  {translate("removeAll")}
                </span>
              </MenuItem>
            )}
            {branches.map((branch) => (
              <MenuItem data-cy={branch.name} value={branch.id} key={branch.id}>
                {branch.name}
              </MenuItem>
            ))}
          </Select>
        </SkeletonComponent>
      </Grid>
      <Grid item container md={4} lg={4} justifyContent="flex-end">
        <Button
          data-cy="buttonAddBranch"
          className={`${classes.addBranchButton} w-100`}
          customColor={Color.SECONDARY}
          onClick={handleAddBranchToList}
          disabled={!selectedBranchesToAdd.length}
        >
          <FiPlusCircle size={18} className="mr-3" />
          {translate("addBranch")}
        </Button>
      </Grid>
    </Grid>
  );

  const renderActionColumn = (index: number) => (
    <IconButton
      disabled={disabledFields}
      onClick={() => {
        if (disabledFields) return;

        const branchesList = selectedBranches;

        branchesList.splice(index, 1);
        setSelectedBranches([...branchesList]);
        if (updateBranchesOnWarehouse) updateBranchesOnWarehouse([...branchesList]);
      }}
    >
      <FiTrash2 size={18} color="#f04438" />
    </IconButton>
  );

  const handleSelectRow = (index: number) => {
    if (rows.includes(index)) {
      const indexOfRowIndexOnArray = rows.indexOf(index);
      const rowsArr = rows;

      rowsArr.splice(indexOfRowIndexOnArray, 1);
      setRows([...rowsArr]);
    } else {
      setRows((state) => [...state, index]);
    }
  };

  const handleCheckAllRow = (checked: boolean) => {
    if (checked) {
      const rowsSelected = Array.from(Array(Number(selectedBranches.length)).keys());

      setRows(rowsSelected);
    } else {
      setRows([]);
    }
  };

  useEffect(() => {
    if (branchesList) setSelectedBranches(branchesList);
  }, [branchesList]);

  return (
    <div>
      <UITable
        titleTable={translate("branches")}
        errors={error && errorMessage ? errorMessage : undefined}
        data={selectedBranches}
        checkField={!isOnViewMode}
        isOnViewMode={isOnViewMode}
        headerActions={isOnViewMode ? undefined : renderHeaderActions()}
        actionColumn={renderActionColumn}
        selectRow={rows}
        onSelectRow={handleSelectRow}
        onSelectRowAll={handleCheckAllRow}
        columns={[
          {
            title: translate("code"),
            columnName: "code",
            spacingColumns: "50px",
            formatComponent: (branchCode) => <BranchCode>{branchCode}</BranchCode>,
          },
          {
            title: translate("name"),
            columnName: "name",
            spacingColumns: "1fr",
          },
        ]}
      />
    </div>
  );
};

export default Branches;
